import axiosClient from "./axiosClient";
import { Pto } from "@models/pto";
import { IPTOBankBalanceDTO } from "@interfaces/IPTOBankBalanceDTO";

export function savePto(data: Partial<Pto>) {
  return axiosClient.post<{ status: string; data: { worker_id: number } }>(
    "/manager/updatepto",
    data
  );
}

/**
 * Fetch PTO bank balances for the current logged-in user
 */
export async function fetchPTOBankBalance(): Promise<IPTOBankBalanceDTO> {
  const response = await axiosClient.get<IPTOBankBalanceDTO>(
    `/pto_api/balances`
  );
  return response.data;
}

/**
 * Fetch PTO bank balances for a specific worker (admin endpoint)
 * @param workerId - The worker ID to fetch balances for
 */
export async function fetchPTOBankBalanceForWorker(
  workerId: number
): Promise<IPTOBankBalanceDTO> {
  const response = await axiosClient.get<IPTOBankBalanceDTO>(
    `/pto_api/admin/balances/${workerId}`
  );
  return response.data;
}

/**
 * Fetch PTO requests for a specific worker
 * @param workerId - The worker ID to fetch requests for
 * @param startYear - Optional four digit year to filter request history (defaults to previous year)
 */
export async function fetchPTORequestsForWorker(
  workerId: number,
  startYear?: number
) {
  const effectiveStartYear =
    typeof startYear === "number" ? startYear : new Date().getUTCFullYear() - 1;
  const response = await axiosClient.get(`/pto_api/pto_contractor_requests`, {
    params: { worker_id: workerId, start_year: effectiveStartYear },
  });
  return response.data;
}

/**
 * Fetch PTO requests for a specific worker that overlap with the given date range
 * @param workerId - The worker ID to fetch requests for
 * @param startDate - Optional start date (YYYY-MM-DD) for range filtering
 * @param endDate - Optional end date (YYYY-MM-DD) for range filtering
 * @returns PTO requests where pto.start_date <= endDate AND pto.end_date >= startDate (overlap logic)
 */
export async function fetchPTORequestsForWorkerPeriod(
  workerId: number,
  startDate?: string,
  endDate?: string
) {
  const response = await axiosClient.get(`/pto_api/pto_contractor_requests`, {
    params: { worker_id: workerId, start_date: startDate, end_date: endDate },
  });
  return response.data;
}

/**
 * Fetch PTO requests for a specific worker (admin endpoint)
 * @param workerId - The worker ID to fetch requests for
 */
export async function fetchPTORequestsForWorkerAdmin(workerId: number) {
  const response = await axiosClient.get(`/pto_api/admin/requests/${workerId}`);
  return response.data;
}

/**
 * Fetch PTO requests for a specific month
 * @param month - Format: YYYY-MM
 */
export async function fetchPtoByMonth() {
  const response = await axiosClient.get(`/pto_api/pto_contractor_requests`);
  return response.data;
}

/**
 * Create new PTO requests
 * @param data - Object containing selections array and optional description
 */
export async function createPtoRequests(data: {
  selections: Array<{ date: string; part: string }>;
  description?: string | null;
  user_id?: number;
  worker_id?: number;
  request_type: string;
}) {
  const { periods } = parseSelections(data.selections);
  if (!periods.length) {
    throw new Error("Select at least one day");
  }

  // Add user_id and description to each period
  const periodsWithMetadata = periods.map((period) => ({
    ...period,
    user_id: data.user_id,
    worker_id: data.worker_id,
    description: data.description || "",
    request_type: data.request_type,
  }));

  // console.log("Submitting PTO periods:", periodsWithMetadata);
  const response = await axiosClient.post(
    "/pto_api/pto_create",
    periodsWithMetadata
  );
  return response.data;
}

function parseSelections(rawSelections: any) {
  // Normalize selections: ensure unique per date, prefer FULL > (AM+PM) > one half
  const dayMap = new Map(); // dateStr -> { part: 'FULL'|'AM'|'PM' }
  for (const sel of Array.isArray(rawSelections) ? rawSelections : []) {
    if (!sel || !sel.date) continue;
    const dateStr = String(sel.date);
    const part = (sel.part || sel.half || sel.type || "FULL").toUpperCase();
    const current = dayMap.get(dateStr);
    if (part === "FULL") {
      dayMap.set(dateStr, { part: "FULL" });
    } else if (part === "AM" || part === "PM") {
      if (!current) {
        dayMap.set(dateStr, { part });
      } else if (current.part !== "FULL" && current.part !== part) {
        // AM + PM together become FULL
        dayMap.set(dateStr, { part: "FULL" });
      }
    }
  }

  // Convert to sorted list of dates with hours
  const items = Array.from(dayMap.entries())
    .map(([dateStr, { part }]) => ({
      date: dateStr,
      hours: part === "FULL" ? 8 : 4,
    }))
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

  // Group consecutive dates into periods
  const periods: Array<{
    start_date: string;
    end_date: string;
    hours: number;
  }> = [];
  if (items.length === 0) return { dayMap, periods };

  let start = items[0].date;
  let end = items[0].date;
  let hours = items[0].hours;

  const nextDate = (dStr: string) => {
    const d = new Date(dStr + "T00:00:00");
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  };

  for (let i = 1; i < items.length; i++) {
    const expected = nextDate(end);
    if (
      new Date(expected + "T00:00:00").getDay() !== 0 &&
      items[i].date === expected
    ) {
      // continues period
      end = items[i].date;
      hours += items[i].hours;
    } else {
      // commit prev
      periods.push({ start_date: start, end_date: end, hours });
      // start new
      start = items[i].date;
      end = items[i].date;
      hours = items[i].hours;
    }
  }
  // commit last
  periods.push({ start_date: start, end_date: end, hours });
  return { dayMap, periods };
}
